import { useCallback, useContext, useMemo, useState } from 'react';
import EAButton from 'components/common/EAButton';
import EAIcon from 'components/common/EAIcon';
import i18next from 'i18next';
import EADialog from 'components/common/EADialog';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from 'services/common/RolesUtils';
import { Context } from 'states/Store';
import EATooltip from 'components/common/EATooltip';
import { getUrlWithAccountId } from 'config/Routes';
import { ERROR, WARNING } from 'services/common/Constants';
import { getCreditsafeThirdpartyDocument } from 'services/edge/CreditsafeService';
import useStyles from "./CreditsafePanelStyle";
import CreditsafeHistoryModal from './CreditsafeHistoryModal';
import { CredifsafeActivityExposure, CredifsafeScore, CreditsafeIndicators, getCreditsafeColorDependingOnScore, parseTextNationalScore } from 'services/common/CreditsafeUtils';
import { theme } from 'style/globalStyle';
import clsx from 'clsx';

const HistoryBtnTooltipTitle = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.historyBtnTooltipTitle}>
      <EAIcon icon="warning-snack" />
      {i18next.t('ecovadis.historyBtnTooltipTitle')}
    </div>
  );
};

const StopButton = ({ stopCreditsafe }) => {
  const { classes } = useStyles();
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const [confirmStopOpen, setConfirmStopOpen] = useState(false);

  const stopHandler = () => {
    stopCreditsafe();
    setConfirmStopOpen(false);
  }

  return (
    <>
      <EAButton
        size="small"
        colorVariant="errorLight"
        onClick={() => setConfirmStopOpen(true)}
        customStyle={{ boxShadow: 'none', minHeight: '30px' }}
        disabled={
          !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CREDITSAFE_SOURCE)
        }
        className={classes.button}
        content={
          <>
            <EAIcon icon="stop_scoring" />
            <span
              className={clsx(classes.buttonStopContent, classes.buttonContent)}
            >
              {i18next.t('creditsafe.deactivation')}
            </span>
          </>
        }
      />

      <EADialog
        isOpen={confirmStopOpen}
        hasActions={false}
        onClose={() => setConfirmStopOpen(false)}
        maxWidth="md"
      >
        <div className={classes.dialogStop}>
          <div className={classes.dialogStopContent}>
            <div className={classes.dialogStopIcon}>
              <EAIcon style={{ width: '60%' }} icon="stop_scoring" />
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600' }}>
              {i18next.t('creditsafe.confirmStopTitle')}
            </div>
          </div>
          <div className={classes.dialogStopActions}>
            <EAButton
              onClick={() => setConfirmStopOpen(false)}
              content={i18next.t('cancel')}
              colorVariant="secondary"
            />
            <EAButton
              onClick={stopHandler}
              content={i18next.t('validate')}
              colorVariant="error"
            />
          </div>
        </div>
      </EADialog>
    </>
  );
}

const ScoresButtons = ({
  scoresHistory = [],
  stopCreditsafe = () => { }, 
  onRiskChange = () => { },
}) => {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [isHistoricDialogOpen, setIsHistoricDialogOpen] = useState(false);

  const goToReport = async () => {
    try {
      const accountId = state.account.id;
      const thirdpartyId = state.thirdparty.thirdpartyId;
      const result = await getCreditsafeThirdpartyDocument(
        state.account.id,
        state.thirdparty.thirdpartyId
      );
      const document = result.content;
      const evidenceId = document?.evidences?.[0]?.responseId;
      const dossierId = document?.dossierId;
      if (evidenceId && dossierId) {
        const docViewerPath = `thirdparties/${thirdpartyId}/documents/${evidenceId}?dossierId=${dossierId}`;
        window.open(getUrlWithAccountId(accountId, docViewerPath), '_blank');
      } else {
        dispatch({
          type: 'ALERT',
          alert: {
            type: WARNING,
            msg: i18next.t("ecovadis.messages.reportInProgress"),
          },
        });
      }
    } catch (error) {
      dispatch({
        type: 'ALERT',
        alert: {
          type: ERROR,
          msg: error?.message?.messages?.error?.[0]?.code || 'INTERNAL_ERROR',
        },
      });
    }
  };

  

  const isHistoryButtonDisabled = () => {
    return scoresHistory?.length < 2;
  };

const parseValueScoreForGraph = (scoreEnum, data) => {
  const rawValue = data[`${scoreEnum}`];

  // Valeurs invalides
  if (!rawValue || rawValue === '-' || rawValue === 'NOT_STARTED' || rawValue === 'NOT_SCORED') {
    return null;
  }

  switch(scoreEnum) {
    case CreditsafeIndicators.INTERNATIONAL_SCORE:
      // A=5 (meilleur), B=4, C=3, D=2, E=1 (moins bon)
      const internationalMap = { 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'E': 1 };
      return internationalMap[rawValue] || null;

    case CreditsafeIndicators.ACTIVITY_SECTOR_EXPOSURE:
      // A=4 (meilleur), B=3, C=2, D=1 (moins bon)
      const activityMap = { 'A': 4, 'B': 3, 'C': 2, 'D': 1 };
      return activityMap[rawValue] || null;

    case CreditsafeIndicators.FINANCIAL_STRENGTH:
      // A1=10, A2=9, A3=8, B1=7, B2=6, B3=5, C1=4, C2=3, C3=2, D=1
      const financialMap = {
        'A1': 10, 'A2': 9, 'A3': 8,
        'B1': 7, 'B2': 6, 'B3': 5,
        'C1': 4, 'C2': 3, 'C3': 2,
        'D': 1
      };
      return financialMap[rawValue] || null;

    case CreditsafeIndicators.CREDIT_LIMIT:
      const creditLimit = data['creditLimitValue'];
      if (!isNaN(creditLimit)) {
        return parseFloat(creditLimit);
      };
      return null;

    case CreditsafeIndicators.NATIONAL_SCORE:
    case CreditsafeIndicators.PAYMENT_BEHAVIOR:
    case CreditsafeIndicators.COMPANY_EXPOSURE:
      if (!isNaN(rawValue)) {
        return parseInt(rawValue);
      };
      return null;

    default:
      return null;
  }
}

const parseTextScoreForGraph = (scoreEnum, data) => {
  const score = data[`${scoreEnum}`];

  switch(scoreEnum) {
    case CreditsafeIndicators.INTERNATIONAL_SCORE:
      return i18next.t(`creditsafe.scoresValues.${CredifsafeScore[score]}.label`)

    case CreditsafeIndicators.ACTIVITY_SECTOR_EXPOSURE:
      return i18next.t(`creditsafe.exposureValues.${CredifsafeActivityExposure[score]}`)

    case CreditsafeIndicators.PAYMENT_BEHAVIOR:
      return score + " " + i18next.t(`creditsafe.days`);
    
    case CreditsafeIndicators.NATIONAL_SCORE:
      if(!isNaN(score)) {
          const intValue = parseInt(score);
          return i18next.t(`creditsafe.localScoresValues.${parseTextNationalScore(intValue)}`);
      } else {
        return i18next.t(`creditsafe.localScoresValues.default`);
      }

    case CreditsafeIndicators.FINANCIAL_STRENGTH:
    case CreditsafeIndicators.CREDIT_LIMIT:
    case CreditsafeIndicators.COMPANY_EXPOSURE:
    default:
      return score;
  }
}

const formatHistoryData = useCallback((result, data) => {
  Object.values(CreditsafeIndicators).forEach((scoreEnum) => {
    if(scoreEnum === CreditsafeIndicators.PREFERENTIAL_RIGHTS) {
      return result;
    }
    if(!data[`${scoreEnum}`] || data[`${scoreEnum}`] === '-') {
      return result;
    }
    const scoreValue = data[`${scoreEnum}`];
    if (scoreValue) {
      if (!result[scoreEnum]) result[scoreEnum] = [];
      result[scoreEnum].push({
        date: new Date(data.date).getTime(),
        year: new Date(data.date).getFullYear(),
        score: parseValueScoreForGraph(scoreEnum, data),
        text: parseTextScoreForGraph(scoreEnum, data),
        bullet: {
          fill: theme.palette.grey.grey100,
          stroke: getCreditsafeColorDependingOnScore(scoreEnum, scoreValue),
        },
        tooltip: {
          textColor: theme.palette.grey.grey100,
          fill: getCreditsafeColorDependingOnScore(scoreEnum, scoreValue),
        },
      });
      result[scoreEnum].filter(item =>  {
            return item !== null && item.date !== null && item.score !== null && !isNaN(item.score)
          });
    }
  });

    return result;
}, []);

const historyGraphData = useMemo(() => {
    return scoresHistory?.sort((a, b) => new Date(a.date) - new Date(b.date))
      ?.reduce((result, scores) => {
        result = formatHistoryData(result, scores)
        return result;
      }, {})
  }, [formatHistoryData, scoresHistory]);



  return (
    <>
      <EAButton
        className={classes.button}
        size="small"
        onClick={goToReport}
        colorVariant="secondary"
        content={
          <>
            <span style={{paddingRight: "4px"}} className={classes.buttonContent}>{i18next.t('ecovadis.goToReport')}</span>
            <EAIcon icon="open-page"/>
          </>
        }
      />

      <EATooltip
        title={isHistoryButtonDisabled() && <HistoryBtnTooltipTitle />}
        variant="light"
        arrow
        placement="top"
      >
          <EAButton
            className={classes.button}
            size="small"
            onClick={() => setIsHistoricDialogOpen(true)}
            disabled={isHistoryButtonDisabled()}
            colorVariant="secondary_dark"
            content={
              <>
                <EAIcon icon="history"/>
                <span style={{paddingLeft: '4px'}} className={classes.buttonContent}>{i18next.t('history')}</span>
              </>
            }
          />
      </EATooltip>

      <StopButton
        className={classes.button}
        stopCreditsafe={stopCreditsafe}
        onRiskChange={onRiskChange}
      />

      <CreditsafeHistoryModal 
      isOpen={isHistoricDialogOpen} 
      historyData={historyGraphData} 
      onClose={() => setIsHistoricDialogOpen(false)} />
    </>
  );
};

export default ScoresButtons;
